import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PenNib, Trash, ArrowUUpLeft } from "@phosphor-icons/react";

const SCRIPT_FONT = "'Segoe Script', 'Brush Script MT', cursive";

const applyStyle = (ctx) => {
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 15;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
};

// Crop the canvas to the bounding box of the drawn ink (with padding) so the
// exported signature fills its field box instead of being scaled down inside
// a mostly-empty canvas (which made it look tiny/faint in the final PDF).
const trimToInk = (src) => {
  const w = src.width, h = src.height;
  const ctx = src.getContext("2d");
  const { data } = ctx.getImageData(0, 0, w, h);
  let minX = w, minY = h, maxX = 0, maxY = 0, found = false;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4 + 3] > 12) {
        found = true;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (!found) return src.toDataURL("image/png");
  const pad = 10;
  minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
  maxX = Math.min(w, maxX + pad); maxY = Math.min(h, maxY + pad);
  const cw = maxX - minX, ch = maxY - minY;
  const out = document.createElement("canvas");
  out.width = cw; out.height = ch;
  out.getContext("2d").drawImage(src, minX, minY, cw, ch, 0, 0, cw, ch);
  return out.toDataURL("image/png");
};

// Draw a single stroke as a smooth quadratic curve through its points.
const drawStroke = (ctx, pts) => {
  if (!pts.length) return;
  if (pts.length < 3) {
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
    ctx.stroke();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length - 1; i++) {
    const mid = { x: (pts[i].x + pts[i + 1].x) / 2, y: (pts[i].y + pts[i + 1].y) / 2 };
    ctx.quadraticCurveTo(pts[i].x, pts[i].y, mid.x, mid.y);
  }
  ctx.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
  ctx.stroke();
};

export default function SignaturePad({ open, onClose, onAdopt, label = "Signature", defaultName = "" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const strokes = useRef([]);      // committed strokes: array of point arrays
  const current = useRef([]);      // points of the stroke in progress
  const [strokeCount, setStrokeCount] = useState(0);
  const [typed, setTyped] = useState(defaultName);
  const [tab, setTab] = useState("draw");

  useEffect(() => {
    setTyped(defaultName);
  }, [defaultName, open]);

  const redrawAll = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    applyStyle(ctx);
    strokes.current.forEach((s) => drawStroke(ctx, s));
  };

  useEffect(() => {
    if (!open || tab !== "draw") return;
    strokes.current = [];
    current.current = [];
    setStrokeCount(0);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    applyStyle(ctx);
  }, [open, tab]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return {
      x: cx * (canvasRef.current.width / rect.width),
      y: cy * (canvasRef.current.height / rect.height),
    };
  };

  const start = (e) => {
    drawing.current = true;
    current.current = [pos(e)];
    applyStyle(canvasRef.current.getContext("2d"));
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    applyStyle(ctx);
    current.current.push(pos(e));
    const pts = current.current;
    const n = pts.length;
    if (n < 3) {
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      ctx.lineTo(pts[n - 1].x, pts[n - 1].y);
      ctx.stroke();
    } else {
      // Draw the newest segment; consecutive segments share endpoints (seamless).
      const p0 = pts[n - 3], p1 = pts[n - 2], p2 = pts[n - 1];
      const mid1 = { x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 };
      const mid2 = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      ctx.beginPath();
      ctx.moveTo(mid1.x, mid1.y);
      ctx.quadraticCurveTo(p1.x, p1.y, mid2.x, mid2.y);
      ctx.stroke();
    }
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (current.current.length) {
      strokes.current.push(current.current);
      current.current = [];
      setStrokeCount(strokes.current.length);
    }
  };

  const undo = () => {
    if (!strokes.current.length) return;
    strokes.current.pop();
    setStrokeCount(strokes.current.length);
    redrawAll();
  };

  const clearCanvas = () => {
    strokes.current = [];
    current.current = [];
    setStrokeCount(0);
    redrawAll();
  };

  const adoptDrawn = () => {
    if (!strokes.current.length) return;
    onAdopt(trimToInk(canvasRef.current));
    onClose();
  };

  const adoptTyped = () => {
    if (!typed.trim()) return;
    const c = document.createElement("canvas");
    c.width = 600;
    c.height = 180;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000000";
    ctx.font = `72px ${SCRIPT_FONT}`;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(typed, c.width / 2, c.height / 2);
    onAdopt(trimToInk(c));
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="signature-pad-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <PenNib size={22} weight="duotone" className="text-action" /> Adopt your {label.toLowerCase()}
          </DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="draw" data-testid="sig-tab-draw">Draw</TabsTrigger>
            <TabsTrigger value="type" data-testid="sig-tab-type">Type</TabsTrigger>
          </TabsList>

          <TabsContent value="draw" className="mt-4">
            <div className="relative rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden">
              <canvas
                ref={canvasRef}
                width={1040}
                height={400}
                className="w-full touch-none"
                style={{ height: 200 }}
                onMouseDown={start}
                onMouseMove={move}
                onMouseUp={end}
                onMouseLeave={end}
                onTouchStart={start}
                onTouchMove={move}
                onTouchEnd={end}
                data-testid="signature-canvas"
              />
              <span className="pointer-events-none absolute bottom-3 left-0 right-0 text-center text-xs text-slate-400">
                Draw {label.toLowerCase()} above
              </span>
              <div className="absolute top-2 right-2 flex items-center gap-3">
                <button
                  onClick={undo}
                  disabled={strokeCount === 0}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="signature-undo-btn"
                >
                  <ArrowUUpLeft size={14} /> Undo
                </button>
                <button
                  onClick={clearCanvas}
                  disabled={strokeCount === 0}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="signature-clear-btn"
                >
                  <Trash size={14} /> Clear
                </button>
              </div>
            </div>
            <Button onClick={adoptDrawn} className="w-full mt-4 rounded-full bg-navy hover:bg-navy/90 h-11" data-testid="adopt-drawn-btn">
              Adopt &amp; Sign
            </Button>
          </TabsContent>

          <TabsContent value="type" className="mt-4">
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type your full name"
              className="h-11"
              data-testid="signature-type-input"
            />
            <div className="mt-4 h-[120px] rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center">
              <span style={{ fontFamily: SCRIPT_FONT, fontSize: 46, color: "#0A0A0A" }}>
                {typed || "Your Signature"}
              </span>
            </div>
            <Button onClick={adoptTyped} className="w-full mt-4 rounded-full bg-navy hover:bg-navy/90 h-11" data-testid="adopt-typed-btn">
              Adopt &amp; Sign
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
